import { formatCurrencyAmountWithIssue } from '../../../commons/currency/Currency.Functions';
import { IconButton, TooltipHost, DirectionalHint } from "@fluentui/react";
import { connect } from "react-redux";
import { useBoolean } from "@fluentui/react-hooks";
import moment from "moment";

// Functions
import * as ComponentFunctions from './SettlementSummaryItem.Functions';

//styles
import './SettlementSummaryItem.css';

// Components
import SettlementDetailsListComponet from "../SettlementDetailsList/SettlementDetailsList.Component";
import SettlementResumeFormComponent from "../SettlementResumeForm/SettlementResumeForm.Component";
import { SettlementSummeryTotal } from "../../../models/Entities/SettlementSummaryTotals/SettlementSummeryTotal";
import { SettlementDetail } from "../../../models/Entities/SettlementDetails/SettlementDetail";
import { conversionLabel, conversionOf, feeBreakdown, formatPercentage, hasFeeBreakdown, rateTypeLabel } from '../../../commons/fee/PractitionerFee.Logic';
import { groupPractitionerDetails, practitionerSubtotals, realizeLabel, visitStatusOf } from './SettlementSummaryItem.Practitioner.Logic';

const SettlementSummaryItem = (props: ComponentFunctions.Props) => {

    const [isDetailsPanelOpen, { setTrue: openDetailsPanel, setFalse: dismissDetailsPanel }] = useBoolean(false);
    const [isOpenSummaryForm, { setTrue: openSummaryFormPanel, setFalse: dismissSummaryFormPanel }] = useBoolean(false);
    const [isCollapsed, { toggle: toggleCollapsed }] = useBoolean(false);

    const fmt = (amount: number, currency: string) => formatCurrencyAmountWithIssue(amount, currency);

    const buildACobrarTooltip = (detail: SettlementDetail) => (
        <div className="ssi-cobrar-tooltip">
            <div className="ssi-cobrar-tooltip-title">Cómo se calcula el importe a cobrar</div>
            <table className="ssi-cobrar-tooltip-table">
                <tbody>
                    <tr>
                        <td>Honorarios centro médico</td>
                        <td>{fmt(detail.ownerAmount, detail.currency)}</td>
                    </tr>
                    <tr className="ssi-cobrar-tooltip-deduct">
                        <td>− Retención centro ({detail.withHolingPercentage}%)</td>
                        <td>− {fmt(detail.ownerAmountWithholding, detail.currency)}</td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr className="ssi-cobrar-tooltip-result">
                        <td>Total a cobrar</td>
                        <td>{fmt(detail.ownerAmountWithholdingTotal, detail.currency)}</td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );

    // Liquidación a médico: de dónde sale cada honorario. MedicalProtocol lo
    // calcula sobre el consumo del protocolo: facturado − retención − costo =
    // base × % pactado.
    const buildFeeTooltip = (detail: SettlementDetail) => {
        const b = feeBreakdown(detail);
        const conversion = conversionOf(detail);
        return (
            <div className="ssi-cobrar-tooltip">
                <div className="ssi-cobrar-tooltip-title">Cómo se calcula el honorario</div>
                <table className="ssi-cobrar-tooltip-table">
                    <tbody>
                        <tr>
                            <td>Facturado al sponsor</td>
                            <td>{fmt(b.source, detail.currency)}</td>
                        </tr>
                        <tr className="ssi-cobrar-tooltip-deduct">
                            <td>− Retención</td>
                            <td>− {fmt(b.withholding, detail.currency)}</td>
                        </tr>
                        <tr className="ssi-cobrar-tooltip-deduct">
                            <td>− Costo descontado</td>
                            <td>− {fmt(b.discountedCost, detail.currency)}</td>
                        </tr>
                        <tr>
                            <td>Base de cálculo</td>
                            <td>{fmt(b.base, detail.currency)}</td>
                        </tr>
                        <tr>
                            <td>× Porcentaje pactado</td>
                            <td>{formatPercentage(b.percentage)}</td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr className="ssi-cobrar-tooltip-result">
                            <td>Honorario</td>
                            <td>{fmt(b.fee, detail.currency)}</td>
                        </tr>
                    </tfoot>
                </table>
                {b.hasManualAdjustment && (
                    <div className="ssi-cobrar-tooltip-note">No coincide con base × %: tiene ajuste manual o cotización.</div>
                )}
                {conversion && (
                    <div className="ssi-cobrar-tooltip-note">
                        Convertido desde {conversionLabel(conversion, fmt)}
                        {rateTypeLabel(conversion.rateType) ? ` (${rateTypeLabel(conversion.rateType)})` : ''}
                    </div>
                )}
            </div>
        );
    };

    const pct = (pctValue: number, amount: number, currency: string) => (
        <><span className="ssi-pct">({pctValue}%)</span>&nbsp;{fmt(amount, currency)}</>
    );

    const Modals = (
        <>
            <SettlementDetailsListComponet
                isOpen={isDetailsPanelOpen}
                openPanel={openDetailsPanel}
                dismissPanel={dismissDetailsPanel}
                SettlementId={props.SettlementId}
                SettlementResumeId={props.CurrentSummary?.id}
            />
            <SettlementResumeFormComponent
                isOpen={isOpenSummaryForm}
                openPanel={openSummaryFormPanel}
                dismissPanel={dismissSummaryFormPanel}
                SettlementId={props.SettlementId}
                SettlementResumeId={props.CurrentSummary?.id}
            />
        </>
    );

    // Group details by concept (standard table)
    const detailsByConceptName: Record<string, SettlementDetail[]> = {};
    (props.SettlementDetails || []).forEach((d) => {
        const key = d.conceptName || 'Sin concepto';
        if (!detailsByConceptName[key]) detailsByConceptName[key] = [];
        detailsByConceptName[key].push(d);
    });

    // Liquidación a médico: grupos por protocolo · etapa y subtotales con base de cálculo
    const practitionerGroups = props.isPractitionerProtocol ? groupPractitionerDetails(props.SettlementDetails, props.ProtocolTitleOf) : [];
    const practitionerGrandTotals = props.isPractitionerProtocol ? practitionerSubtotals(props.SettlementDetails || []) : [];
    const practitionerHasBase = (props.SettlementDetails || []).some(hasFeeBreakdown);

    // Grand totals grouped by currency
    const grandTotals: Record<string, { bruto: number; retencionCentro: number; retencionEquipo: number; centro: number; equipo: number; aCobrar: number }> = {};
    (props.SettlementDetails || []).forEach((d) => {
        const cur = (d.currency || 'ARS').toUpperCase();
        if (!grandTotals[cur]) grandTotals[cur] = { bruto: 0, retencionCentro: 0, retencionEquipo: 0, centro: 0, equipo: 0, aCobrar: 0 };
        grandTotals[cur].bruto           += d.amount                        || 0;
        grandTotals[cur].retencionCentro += d.ownerAmountWithholding         || 0;
        grandTotals[cur].retencionEquipo += d.teamAmountWithholding          || 0;
        grandTotals[cur].centro          += d.ownerAmount                    || 0;
        grandTotals[cur].equipo          += d.teamAmount                     || 0;
        grandTotals[cur].aCobrar         += d.ownerAmountWithholdingTotal    || 0;
    });

    return (
        <div className={'ssi-card' + (props.CurrentSummary?.haveWarning ? ' ssi-card--warning' : '')}>

            {/* ── Card header ─────────────────────────────────────────── */}
            <div className={`ssi-header${isCollapsed ? ' ssi-header--collapsed' : ''}`} onClick={toggleCollapsed}>
                <div className="ssi-header-left">
                    <div className="ssi-header-title-row">
                        {props.CurrentSummary?.haveWarning && (
                            <span className="ssi-warning-dot" title="Tiene advertencias">⚠</span>
                        )}
                        <h3 className="ssi-title">{props.CurrentSummary?.description}</h3>
                        {props.CurrentSummary?.comments && (
                            <span className="ssi-comments">{props.CurrentSummary.comments}</span>
                        )}
                    </div>
                </div>
                <div className="ssi-header-right">
                    {props.CurrentSummary?.totals && props.CurrentSummary.totals.length > 0 && (
                        <div className="ssi-header-totals">
                            {props.CurrentSummary.totals.map((total: SettlementSummeryTotal) => {
                                const totalACobrar = total.total - total.teamAmount - total.ownerAmountWithholding;
                                return (
                                    <div key={total.id} className="ssi-header-total-item">
                                        <span className="ssi-header-total-currency">{total.currencyCode.toUpperCase()}</span>
                                        <div className="ssi-header-total-amounts">
                                            <span className="ssi-header-total-bruto">{fmt(total.total, total.currencyCode)}</span>
                                            <span className="ssi-header-total-arrow">→</span>
                                            <span className="ssi-header-total-net">{fmt(totalACobrar, total.currencyCode)}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    <IconButton
                        iconProps={{ iconName: isCollapsed ? 'ChevronRight' : 'ChevronDown' }}
                        title={isCollapsed ? 'Expandir' : 'Colapsar'}
                        onClick={(e) => { e.stopPropagation(); toggleCollapsed(); }}
                        className="ssi-collapse-btn"
                    />
                </div>
            </div>

            {/* ── Detail rows table ────────────────────────────────────── */}
            {!isCollapsed && props.SettlementDetails && props.SettlementDetails.length > 0 && (
                props.isPractitionerProtocol ? (
                    /* ── Liquidación a médico: Concepto / Paciente / Fecha / Modalidad / Cantidad / [Base de cálculo / %] / Honorario.
                          La base y el % sólo aparecen cuando el servidor informó de dónde sale el honorario. */
                    <div className="ssi-table-wrapper">
                        <table className="ssi-table ssi-table--pract">
                            <thead>
                                <tr className="ssi-thead-group">
                                    <th>Concepto</th>
                                    <th>Paciente</th>
                                    <th>Fecha de realización</th>
                                    <th>Modalidad</th>
                                    <th>Estado HC</th>
                                    <th className="ssi-col-num ssi-col-num-start">Cantidad</th>
                                    {practitionerHasBase && <th className="ssi-col-num">Base de cálculo</th>}
                                    {practitionerHasBase && <th className="ssi-col-num">%</th>}
                                    <th className="ssi-col-num ssi-th-collect">Honorario</th>
                                </tr>
                            </thead>
                            <tbody>
                                {practitionerGroups.flatMap((group) => {
                                    const subtotals = practitionerSubtotals(group.items);
                                    const labelSpan = 5;
                                    const columnCount = practitionerHasBase ? 9 : 7;
                                    return [
                                        <tr key={`group-${group.key}`} className="ssi-concept-section">
                                            <td colSpan={columnCount} className="ssi-concept-section-label">
                                                {group.label}
                                                <span className="ssi-concept-count">{group.items.length}</span>
                                            </td>
                                        </tr>,
                                        ...group.items.map((detail: SettlementDetail) => {
                                            const withBase = hasFeeBreakdown(detail);
                                            const breakdown = feeBreakdown(detail);
                                            const visit = visitStatusOf(detail.visitStatus);
                                            const conversion = conversionOf(detail);
                                            const conversionType = conversion ? rateTypeLabel(conversion.rateType) : undefined;
                                            const feeValue = (
                                                <span className="ssi-fee-cell">
                                                    <span className={`ssi-cobrar-value${breakdown.hasManualAdjustment && withBase ? ' ssi-fee--adjusted' : ''}`}>
                                                        {fmt(detail.amount, detail.currency)}
                                                    </span>
                                                    {conversion && (
                                                        <span className="ssi-fee-conv" title={conversionType ? `Convertido con ${conversionType}` : 'Convertido'}>
                                                            {conversionLabel(conversion, fmt)}
                                                        </span>
                                                    )}
                                                </span>
                                            );
                                            return (
                                                <tr key={detail.id} className="ssi-detail-row">
                                                    <td>{detail.conceptName || '—'}</td>
                                                    <td>
                                                        <span className="ssi-patient-chip">
                                                            {detail.patientNumberInProtocol || String(detail.patientRecordNumber) || '—'}
                                                        </span>
                                                    </td>
                                                    <td className="ssi-date">
                                                        {detail.dueDate ? moment(detail.dueDate).format('DD/MM/YYYY') : '—'}
                                                    </td>
                                                    <td>{realizeLabel(detail)}</td>
                                                    <td>
                                                        {visit ? (
                                                            <span className={`ssi-visit ssi-visit--${visit.variant}`} title={`Visita ${visit.label.toLowerCase()} en la historia clínica`}>
                                                                <span className="ssi-visit__dot" />
                                                                {visit.label}
                                                            </span>
                                                        ) : <span className="ssi-muted">—</span>}
                                                    </td>
                                                    <td className="ssi-amount ssi-col-num-start">{detail.quantityToSettle ?? 1}</td>
                                                    {practitionerHasBase && (
                                                        <td className="ssi-amount ssi-fee-base">
                                                            {withBase ? fmt(breakdown.base, detail.currency) : <span className="ssi-muted">—</span>}
                                                        </td>
                                                    )}
                                                    {practitionerHasBase && (
                                                        <td className="ssi-amount ssi-fee-pct">
                                                            {withBase && breakdown.percentage > 0 ? formatPercentage(breakdown.percentage) : <span className="ssi-muted">—</span>}
                                                        </td>
                                                    )}
                                                    <td className="ssi-amount ssi-amount--highlight">
                                                        {withBase || conversion ? (
                                                            <TooltipHost
                                                                content={buildFeeTooltip(detail)}
                                                                directionalHint={DirectionalHint.bottomRightEdge}
                                                                calloutProps={{ gapSpace: 4, beakWidth: 8 }}
                                                            >
                                                                {feeValue}
                                                            </TooltipHost>
                                                        ) : feeValue}
                                                    </td>
                                                </tr>
                                            );
                                        }),
                                        ...subtotals.map((subtotal) => (
                                            <tr key={`subtotal-${group.key}-${subtotal.currency}`} className="ssi-concept-subtotal">
                                                <td colSpan={labelSpan} className="ssi-subtotal-label">
                                                    Subtotal{subtotals.length > 1 ? ` ${subtotal.currency}` : ''}
                                                </td>
                                                <td className="ssi-subtotal-amount ssi-col-num-start">{subtotal.qty}</td>
                                                {practitionerHasBase && <td className="ssi-subtotal-amount">{fmt(subtotal.base, subtotal.currency)}</td>}
                                                {practitionerHasBase && <td className="ssi-subtotal-amount" />}
                                                <td className="ssi-subtotal-amount ssi-subtotal-highlight">{fmt(subtotal.fee, subtotal.currency)}</td>
                                            </tr>
                                        )),
                                    ];
                                })}
                            </tbody>
                            {practitionerGrandTotals.map((gt) => (
                                <tfoot key={`grand-${gt.currency}`}>
                                    <tr className="ssi-grand-total">
                                        <td colSpan={5} className="ssi-grand-total-label">
                                            Total general{practitionerGrandTotals.length > 1 ? ` ${gt.currency}` : ''}
                                        </td>
                                        <td className="ssi-grand-total-amount ssi-col-num-start">{gt.qty}</td>
                                        {practitionerHasBase && <td className="ssi-grand-total-amount">{fmt(gt.base, gt.currency)}</td>}
                                        {practitionerHasBase && <td className="ssi-grand-total-amount" />}
                                        <td className="ssi-grand-total-amount ssi-grand-total-highlight">{fmt(gt.fee, gt.currency)}</td>
                                    </tr>
                                </tfoot>
                            ))}
                        </table>
                    </div>
                ) : (
                    /* ── Standard table: Paciente / Fecha / Etapa / Bruto / Retención / División de honorarios / A cobrar */
                    <div className="ssi-table-wrapper">
                        <table className="ssi-table">
                            <thead>
                                <tr className="ssi-thead-group">
                                    <th rowSpan={2}>Paciente</th>
                                    <th rowSpan={2}>Fecha</th>
                                    <th rowSpan={2}>Etapa</th>
                                    <th rowSpan={2}>Total bruto</th>
                                    <th colSpan={2} className="ssi-th-group ssi-th-ret">Retención</th>
                                    <th colSpan={2} className="ssi-th-group">División de honorarios</th>
                                    <th rowSpan={2} className="ssi-th-collect">A cobrar</th>
                                </tr>
                                <tr className="ssi-thead-sub">
                                    <th className="ssi-th-ret">Centro</th>
                                    <th className="ssi-th-ret">Equipo</th>
                                    <th>Centro</th>
                                    <th>Equipo médico</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(detailsByConceptName).flatMap(([conceptName, details]) => {
                                    const cur = details[0]?.currency || 'ARS';
                                    const subBruto            = details.reduce((s, d) => s + (d.amount                  || 0), 0);
                                    const subRetencionCentro  = details.reduce((s, d) => s + (d.ownerAmountWithholding   || 0), 0);
                                    const subRetencionEquipo  = details.reduce((s, d) => s + (d.teamAmountWithholding    || 0), 0);
                                    const subCentro           = details.reduce((s, d) => s + (d.ownerAmount              || 0), 0);
                                    const subEquipo           = details.reduce((s, d) => s + (d.teamAmount               || 0), 0);
                                    const subACobrar          = details.reduce((s, d) => s + (d.ownerAmountWithholdingTotal || 0), 0);
                                    return [
                                        <tr key={`group-${conceptName}`} className="ssi-concept-section">
                                            <td colSpan={9} className="ssi-concept-section-label">
                                                {conceptName}
                                                <span className="ssi-concept-count">{details.length}</span>
                                            </td>
                                        </tr>,
                                        ...details.map((detail: SettlementDetail) => (
                                            <tr key={detail.id} className="ssi-detail-row">
                                                <td>
                                                    <span className="ssi-patient-chip">{detail.patientNumberInProtocol}</span>
                                                </td>
                                                <td className="ssi-date">
                                                    {detail.dueDate ? moment(detail.dueDate).format('DD/MM/YYYY') : '—'}
                                                </td>
                                                <td className="ssi-stage">
                                                    {detail.contractStageName && (
                                                        <span className="ssi-stage-badge">{detail.contractStageName}</span>
                                                    )}
                                                </td>
                                                <td className="ssi-amount">{fmt(detail.amount, detail.currency)}</td>
                                                <td className="ssi-withholding ssi-amount--ret">{fmt(detail.ownerAmountWithholding, detail.currency)}</td>
                                                <td className="ssi-withholding ssi-amount--ret">{fmt(detail.teamAmountWithholding, detail.currency)}</td>
                                                <td className="ssi-fee">{pct(detail.feeDivisionPercentage, detail.ownerAmount, detail.currency)}</td>
                                                <td className="ssi-fee">{pct(detail.teamDivisionPercentage, detail.teamAmount, detail.currency)}</td>
                                                <td className="ssi-amount ssi-amount--highlight">
                                                    <TooltipHost
                                                        content={buildACobrarTooltip(detail)}
                                                        directionalHint={DirectionalHint.bottomRightEdge}
                                                        calloutProps={{ gapSpace: 4, beakWidth: 8 }}
                                                    >
                                                        <span className="ssi-cobrar-value">
                                                            {fmt(detail.ownerAmountWithholdingTotal, detail.currency)}
                                                        </span>
                                                    </TooltipHost>
                                                </td>
                                            </tr>
                                        )),
                                        <tr key={`subtotal-${conceptName}`} className="ssi-concept-subtotal">
                                            <td colSpan={3} className="ssi-subtotal-label">Subtotal</td>
                                            <td className="ssi-subtotal-amount">{fmt(subBruto, cur)}</td>
                                            <td className="ssi-subtotal-amount ssi-amount--ret">{fmt(subRetencionCentro, cur)}</td>
                                            <td className="ssi-subtotal-amount ssi-amount--ret">{fmt(subRetencionEquipo, cur)}</td>
                                            <td className="ssi-subtotal-amount">{fmt(subCentro, cur)}</td>
                                            <td className="ssi-subtotal-amount">{fmt(subEquipo, cur)}</td>
                                            <td className="ssi-subtotal-amount ssi-subtotal-highlight">{fmt(subACobrar, cur)}</td>
                                        </tr>
                                    ];
                                })}
                            </tbody>
                                    {Object.entries(grandTotals).map(([cur, gt]) => (
                                        <tfoot key={`grand-${cur}`}>
                                            <tr className="ssi-grand-total">
                                                <td colSpan={3} className="ssi-grand-total-label">
                                                    Total general{Object.keys(grandTotals).length > 1 ? ` ${cur}` : ''}
                                                </td>
                                                <td className="ssi-grand-total-amount">{fmt(gt.bruto, cur)}</td>
                                                <td className="ssi-grand-total-amount ssi-amount--ret">{fmt(gt.retencionCentro, cur)}</td>
                                                <td className="ssi-grand-total-amount ssi-amount--ret">{fmt(gt.retencionEquipo, cur)}</td>
                                                <td className="ssi-grand-total-amount">{fmt(gt.centro, cur)}</td>
                                                <td className="ssi-grand-total-amount">{fmt(gt.equipo, cur)}</td>
                                                <td className="ssi-grand-total-amount ssi-grand-total-highlight">{fmt(gt.aCobrar, cur)}</td>
                                            </tr>
                                        </tfoot>
                                    ))}
                        </table>
                    </div>
                )
            )}

            {/* ── Invoice totals footer ────────────────────────────────── */}
            {!isCollapsed && props.CurrentSummary?.totals && props.CurrentSummary.totals.length > 0 && (
                props.isPractitionerProtocol ? (
                    /* Simplified footer for practitioner protocol: no honorarios / no retention breakdown */
                    <div className="ssi-invoice-footer">
                        {props.CurrentSummary.totals.map((total: SettlementSummeryTotal) => {
                            const totalACobrar = total.total - total.totalWithholding;
                            return (
                                <div key={total.id} className="ssi-invoice-totals">
                                    <div className="ssi-invoice-currency">{total.currencyCode.toUpperCase()}</div>
                                    <table className="ssi-invoice-table">
                                        <tbody>
                                            <tr className="ssi-inv-bruto">
                                                <td className="ssi-inv-label">Total a liquidar</td>
                                                <td className="ssi-inv-amount">{fmt(total.total, total.currencyCode)}</td>
                                            </tr>
                                            <tr className="ssi-inv-retention">
                                                <td className="ssi-inv-label">Retención</td>
                                                <td className="ssi-inv-amount ssi-inv-neg">− {fmt(total.totalWithholding, total.currencyCode)}</td>
                                            </tr>
                                            <tr className="ssi-inv-total">
                                                <td className="ssi-inv-label">Total a cobrar</td>
                                                <td className="ssi-inv-amount ssi-inv-pos">{fmt(totalACobrar, total.currencyCode)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    /* Standard footer with full honorarios / retention breakdown */
                    <div className="ssi-invoice-footer">
                        {props.CurrentSummary.totals.map((total: SettlementSummeryTotal) => {
                            const totalACobrar = total.total - total.teamAmount - total.ownerAmountWithholding;
                            return (
                                <div key={total.id} className="ssi-invoice-totals">
                                    <div className="ssi-invoice-currency">{total.currencyCode.toUpperCase()}</div>
                                    <table className="ssi-invoice-table">
                                        <tbody>

                                            {/* ── Composición del total a liquidar ── */}
                                            <tr className="ssi-inv-bruto">
                                                <td className="ssi-inv-label">Total a liquidar</td>
                                                <td className="ssi-inv-amount">{fmt(total.total, total.currencyCode)}</td>
                                            </tr>
                                            <tr className="ssi-inv-sub">
                                                <td className="ssi-inv-label">Honorarios centro médico</td>
                                                <td className="ssi-inv-amount">{fmt(total.ownerAmount, total.currencyCode)}</td>
                                            </tr>
                                            <tr className="ssi-inv-sub">
                                                <td className="ssi-inv-label">Honorarios equipo médico</td>
                                                <td className="ssi-inv-amount">{fmt(total.teamAmount, total.currencyCode)}</td>
                                            </tr>

                                            {/* ── Composición de la retención ────── */}
                                            <tr className="ssi-inv-retention">
                                                <td className="ssi-inv-label">Retención total</td>
                                                <td className="ssi-inv-amount">{fmt(total.totalWithholding, total.currencyCode)}</td>
                                            </tr>
                                            <tr className="ssi-inv-sub">
                                                <td className="ssi-inv-label">Retención centro médico</td>
                                                <td className="ssi-inv-amount">{fmt(total.ownerAmountWithholding, total.currencyCode)}</td>
                                            </tr>
                                            <tr className="ssi-inv-sub">
                                                <td className="ssi-inv-label">Retención equipo médico</td>
                                                <td className="ssi-inv-amount">{fmt(total.teamAmountWithholding, total.currencyCode)}</td>
                                            </tr>

                                            {/* ── Deducciones para total a cobrar ── */}
                                            <tr className="ssi-inv-deduct ssi-inv-deduct-first">
                                                <td className="ssi-inv-label">Honorarios equipo médico</td>
                                                <td className="ssi-inv-amount ssi-inv-neg">− {fmt(total.teamAmount, total.currencyCode)}</td>
                                            </tr>
                                            <tr className="ssi-inv-deduct">
                                                <td className="ssi-inv-label">Retención centro médico</td>
                                                <td className="ssi-inv-amount ssi-inv-neg">− {fmt(total.ownerAmountWithholding, total.currencyCode)}</td>
                                            </tr>

                                            {/* ── Total a cobrar ─────────────────── */}
                                            <tr className="ssi-inv-total">
                                                <td className="ssi-inv-label">Total a cobrar</td>
                                                <td className="ssi-inv-amount ssi-inv-pos">{fmt(totalACobrar, total.currencyCode)}</td>
                                            </tr>

                                        </tbody>
                                    </table>
                                </div>
                            );
                        })}
                    </div>
                )
            )}

            {Modals}
        </div>
    );
}

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(SettlementSummaryItem as any);

